// dalleService.backend.js (new, unified)
import { fetchWithTrace } from '../fetchWithTrace';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

async function postJSON(path, body) {
  const token = localStorage.getItem('token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const { data } = await fetchWithTrace(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: body ?? {},
  });
  return data;
}

// Stable keys (match backend TEMPLATE_REGISTRY)
export const ImageTemplates = Object.freeze({
  PROMPT_AND_CONTEXT: 'promptAndContext',
  SCENE: 'scene',
  SCENE_WITH_REFS: 'sceneWithRefs',
  ARTIFACT_WITH_REFS: 'imageArtifactWithRefs',
  CHARACTER: 'character',
  RAW: 'raw',
});

/**
 * Generate an image via the unified backend.
 * @param {Object} opts
 * @param {"promptAndContext"|"withDetails"|"character"|"raw"} opts.template
 * @param {string} opts.model - e.g. "gpt-image-1", "dall-e-3", future models
 * @param {string} [opts.size] - optional; if omitted backend uses model default
 * @param {Object} [opts.params] - fields required by the template
 *   - promptAndContext: { sceneDescription, context }
 *   - withDetails: { sceneDescription, context, characterBible, environment, characterExpressions }
 *   - character: { character }
 *   - raw: { prompt, context }
 * @returns {Promise<{data: string, format: 'b64'|'url', legacy: boolean, model: string, size?: string}>}
 */
export async function generateImage({ template, model, size, params = {} }) {
  const payload = { template, model, ...(size ? { size } : {}), ...params };
  const json = await postJSON('/api/images/generateImage', payload);

  // Normalize regardless of model/return shape
  const isB64 = Object.prototype.hasOwnProperty.call(json, 'b64');
  const data = isB64 ? json.b64 : json.url ?? '';
  // const format = isB64 ? 'b64' : 'url';
  // const legacy = Boolean(json.legacy);

  //return { data, format, legacy, model, size };
  return data;
}

/* Optional tiny convenience wrappers (use if they improve readability): */

export const ImageAPI = {
  promptAndContext({ sceneDescription, context, model = 'gpt-image-1.5', size } = {}) {
    return generateImage({
      template: ImageTemplates.PROMPT_AND_CONTEXT,
      model,
      size,
      params: { sceneDescription, context },
    });
  },
  scene({ sceneDescription, context, characterBible, environment, characterExpressions, characterAttire, model = 'gpt-image-1', size } = {}) {
    return generateImage({
      template: ImageTemplates.SCENE,
      model,
      size,
      params: { sceneDescription, context, characterBible, environment, characterExpressions, characterAttire },
    });
  },
  sceneWithRefs({ sceneDescription, context, characterBible, environment, characterExpressions, characterAttire, model = 'gpt-image-1', size } = {}) {
    return generateImage({
      template: ImageTemplates.SCENE_WITH_REFS,
      model,
      size,
      params: { sceneDescription, context, characterBible, environment, characterExpressions, characterAttire },
    });
  },
  artifactWithRefs({ prompt, context, images, model = 'gemini-3-pro-image-preview', size } = {}) {
    return generateImage({
      template: ImageTemplates.ARTIFACT_WITH_REFS,
      model,
      size,
      params: { prompt, context, images },
    });
  },
  character({ character, model = 'gpt-image-1', size } = {}) {
    return generateImage({
      template: ImageTemplates.CHARACTER,
      model,
      size,
      params: { character },
    });
  },
  raw({ prompt, context, model = 'gpt-image-1', size } = {}) {
    return generateImage({
      template: ImageTemplates.RAW,
      model,
      size,
      params: { prompt, context },
    });
  },
};

// --- ADD BELOW existing exports in imageService.backend.js ---

/**
 * Generate images for every shot in a scene (beat → shots).
 * Sends the full `scene` object, plus optional knobs.
 * Response shape matches backend: { sceneNumber, totalBeats, totalShots, results: [ { beat, summary, shots:[{ id, prompt, b64|url, seed, ... }] } ], elapsed_ms }
 */
export async function generateSceneShotImages({
  scene,
  model,      // optional; fallbacks on the backend
  size,       // optional
  context,    // optional global context string
  characterBible, // optional; Map or object (pass through)
  projectId,  // optional; for logging/correlation
  maxWorkers, // optional; parallelism hint
} = {}) {
  const payload = {
    ...(projectId ? { projectId } : {}),
    ...(model ? { model } : {}),
    ...(size ? { size } : {}),
    ...(context ? { context } : {}),
    ...(characterBible ? { characterBible } : {}),
    ...(maxWorkers ? { max_workers: maxWorkers } : {}),
    scene
  };
  return postJSON('/api/images/generateSceneShotImages', payload);
}


export async function generateSceneShotImageEdits({
  scene,
  model,      // optional; fallbacks on the backend
  size,       // optional
  context,    // optional global context string
  characterBible, // optional; Map or object (pass through)
  projectId,  // optional; for logging/correlation
  maxWorkers, // optional; parallelism hint
} = {}) {
  const payload = {
    ...(projectId ? { projectId } : {}),
    ...(model ? { model } : {}),
    ...(size ? { size } : {}),
    ...(context ? { context } : {}),
    ...(characterBible ? { characterBible } : {}),
    ...(maxWorkers ? { max_workers: maxWorkers } : {}),
    scene
  };
  return postJSON('/api/images/generateSceneShotImageEdits', payload);
}
